import { useMemo, useEffect, useState, isValidElement } from "react";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

import { useMaterialUIController } from "context";

// Sade zebra: beyaz / çok açık gri (önceki camgöbeği-gri şeritler yerine)
const ROW_COLORS = ["#FFFFFF", "#F8FAFC"];
const ROW_COLORS_DARK = ["rgba(56,189,248,0.06)", "rgba(148,163,184,0.04)"];

function normalizeString(str) {
  if (!str) return "";
  return String(str)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[çÇ]/g, "c")
      .replace(/[ğĞ]/g, "g")
      .replace(/[ıİI]/g, "i")
      .replace(/[öÖ]/g, "o")
      .replace(/[şŞ]/g, "s")
      .replace(/[üÜ]/g, "u")
      .trim();
}

// Visible text of a cell. Many pages pass JSX (links, chips, tooltips) as cell values, which
// String() turns into "[object Object]", so search never matched them.
function cellText(value) {
  if (value === null || value === undefined || typeof value === "boolean") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(cellText).join(" ");
  if (isValidElement(value)) {
    const { children, title, label } = value.props || {};
    return [title, label, children].map(cellText).join(" ");
  }
  return "";
}

/* ------------------ 🔍 Global filtre (tüm kolonlarda, Türkçe uyumlu) ------------------ */
function globalFilterFn(rows, columnIds, filterValue) {
  const normalizedFilter = normalizeString(filterValue);
  if (!normalizedFilter) return rows;

  return rows.filter((row) =>
      columnIds.some((id) => {
        const cellValue = row.values[id];
        const normalizedCell = normalizeString(cellText(cellValue));
        return normalizedCell.includes(normalizedFilter);
      })
  );
}
function DataTable({
                     entriesPerPage = DEFAULT_DATA_TABLE_ENTRIES_PER_PAGE,
                     canSearch = false,
                     showTotalEntries = true,
                     table,
                     pagination = DEFAULT_DATA_TABLE_PAGINATION,
                     isSorted = true,
                     noEndBorder = false,
                       disableInternalSort = false,
                       onRowClick = null
                   }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const defaultValue = entriesPerPage.defaultValue ?? 10;
  const baseEntries = entriesPerPage.entries?.map((el) => el.toString()) ?? ["5", "10", "15", "20", "25"];
  // Varsayılan sayfa boyutu listede yoksa (ör. defaultValue: 100) seçici boş görünür ve MUI uyarır; listeye ekle
  const entries = baseEntries.includes(String(defaultValue))
      ? baseEntries
      : [...baseEntries, String(defaultValue)].sort((a, b) => Number(a) - Number(b));

  const columns = useMemo(() => table.columns, [table]);

    const data = useMemo(() => {
        const rows = [...(table.rows || [])];

        if (disableInternalSort) {
            return rows; // 👈 DOKUNMA
        }

        return rows.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA;
        });
    }, [table.rows, disableInternalSort]);

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: {
                pageIndex: 0,
                pageSize: defaultValue,
                ...(table?.initialState || {
                    sortBy: [{ id: "created_at", desc: true }],
                }),
            },

            globalFilter: globalFilterFn,
            // Parents rebuild table.rows on every render. react-table's auto resets run in a layout
            // effect on each new `data` and dispatch synchronously, which chained into React's
            // "Maximum update depth exceeded" (#185) on busy pages. The page reset below covers it.
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetGlobalFilter: false,
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

    useEffect(() => {
        if (pageIndex !== 0) gotoPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    useEffect(() => {
        setPageSize(defaultValue);
        gotoPage(0);
    }, [defaultValue]);

  const setEntriesPerPage = (value) => {
    setPageSize(value);
    gotoPage(0);
  };

  const renderPagination = pageOptions.map((option) => (
      <MDPagination
          item
          key={option}
          onClick={() => gotoPage(Number(option))}
          active={pageIndex === option}
      >
        {option + 1}
      </MDPagination>
  ));

  const [search, setSearch] = useState(globalFilter || "");

  const setSortedValue = (column) => {
    if (!isSorted) return false;
    if (!column.isSorted) return "none";
    return column.isSortedDesc ? "desc" : "asce";
  };

  const entriesStart = rows.length === 0 ? 0 : pageIndex * pageSize + 1;
  const entriesEnd = Math.min(pageSize * (pageIndex + 1), rows.length);

  const COLORS = darkMode ? ROW_COLORS_DARK : ROW_COLORS;

  return (
      <TableContainer sx={{ boxShadow: "none" }}>
        {(entriesPerPage || canSearch) && (
            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              {entriesPerPage && (
                  <MDBox display="flex" alignItems="center">
                    <Autocomplete
                        disableClearable
                        value={pageSize.toString()}
                        options={entries}
                        onChange={(event, newValue) => {
                          setEntriesPerPage(parseInt(newValue, 10));
                        }}
                        size="small"
                        sx={{ width: "5rem" }}
                        renderInput={(params) => <MDInput {...params} />}
                    />
                    <MDTypography variant="caption" color="secondary">
                      &nbsp;&nbsp;entries per page
                    </MDTypography>
                  </MDBox>
              )}
              {canSearch && (
                  <MDBox width="12rem" ml="auto">
                    <MDInput
                        placeholder="Search..."
                        value={search}
                        size="small"
                        fullWidth
                        onChange={({ currentTarget }) => {
                          const value = currentTarget.value;
                          setSearch(value);
                          setGlobalFilter(value || undefined); // her karakterde çalışır
                        }}
                    />
                  </MDBox>
              )}
            </MDBox>
        )}

        <Table
            {...getTableProps()}
            sx={{
              borderCollapse: "separate",
              "tbody td": {
                  borderRight: darkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)"
              },
              "tbody td:last-child": {
                borderRight: "none",
              },
            }}
        >
          <MDBox component="thead">
            {/* react-table'ın get*Props() sonuçları key içerir; key spread ile değil doğrudan verilmeli
                (React 18.3 uyarısı). headerGroup'un id'si olmadığı için key prop'larından alınır. */}
            {headerGroups.map((headerGroup, headerGroupIndex) => {
              const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                  <TableRow key={headerGroupKey ?? headerGroupIndex} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: columnKey, ...columnProps } = column.getHeaderProps(
                          isSorted && column.getSortByToggleProps()
                      );
                      return (
                          <DataTableHeadCell
                              key={column.id ?? columnKey}
                              {...columnProps}
                              width={column.width || "auto"}
                              align={column.align || "left"}
                              sorted={setSortedValue(column)}
                          >
                            {column.render("Header")}
                          </DataTableHeadCell>
                      );
                    })}
                  </TableRow>
              );
            })}
          </MDBox>

          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              prepareRow(row);
              const rowColor = COLORS[key % COLORS.length];

              const { key: rowKey, ...rowProps } = row.getRowProps();

              return (
                  <TableRow
                      key={row.id ?? rowKey}
                      {...rowProps}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      sx={{
                        backgroundColor: rowColor,
                        cursor: onRowClick ? "pointer" : undefined,
                        transition: "background-color .12s ease",
                        "&:hover": { backgroundColor: darkMode ? "rgba(148,163,184,0.10)" : "#EEF4FF" },
                      }}
                  >
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      return (
                          <DataTableBodyCell
                              key={cell.column.id ?? cellKey}
                              noBorder={noEndBorder && rows.length - 1 === key}
                              align={cell.column.align || "left"}
                              {...cellProps}
                          >
                            {cell.render("Cell")}
                          </DataTableBodyCell>
                      );
                    })}
                  </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <MDBox
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        >
          {showTotalEntries && (
              <MDBox mb={{ xs: 3, sm: 0 }}>
                <MDTypography variant="button" color="secondary" fontWeight="regular">
                  Showing {entriesStart} to {entriesEnd} of {rows.length} entries
                </MDTypography>
              </MDBox>
          )}
          {pageOptions.length > 1 && (
              <MDPagination
                  variant={pagination.variant || "gradient"}
                  color={pagination.color || "info"}
              >
                {canPreviousPage && (
                    <MDPagination item onClick={() => previousPage()}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                )}
                {renderPagination.length > 6 ? (
                    <MDBox width="5rem" mx={1}>
                      <MDInput
                          inputProps={{
                            type: "number",
                            min: 1,
                            max: pageOptions.length,
                          }}
                          value={pageIndex + 1}
                          onChange={({ target: { value } }) => {
                            const page = Number(value) - 1;
                            if (page >= 0 && page < pageOptions.length) gotoPage(page);
                          }}
                      />
                    </MDBox>
                ) : (
                    renderPagination
                )}
                {canNextPage && (
                    <MDPagination item onClick={() => nextPage()}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                )}
              </MDPagination>
          )}
        </MDBox>
      </TableContainer>
  );
}

/* ------------------ ⚙️ Default Props ------------------ */
// Varsayılan değerler (eski DataTable.defaultProps; tek örnek olarak korunur)
const DEFAULT_DATA_TABLE_ENTRIES_PER_PAGE = { defaultValue: 20, entries: [5, 10, 20, 50, 100] };
const DEFAULT_DATA_TABLE_PAGINATION = { variant: "gradient", color: "info" };

export default DataTable;
